import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import DoctorSidebar from "./DoctorSidebar";
import api from "../utils/api";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert, MenuItem, Select, FormControl, InputLabel, Tooltip
} from "@mui/material";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend
} from "recharts";
import { Download, Payment as PaymentIcon, GetApp } from "@mui/icons-material";
import "./DoctorEarnings.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const DoctorEarnings = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState({
    todaysEarnings: 0,
    previousEarnings: { weekly: 0, monthly: 0, yearly: 0 },
    totalEarnings: 0,
    totalAmountAvailableForWithdrawal: 0,
    totalWithdrawn: 0,
    nextPayoutDate: null,
  });
  const [breakdown, setBreakdown] = useState([]);
  const [filteredBreakdown, setFilteredBreakdown] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [bankDetailsOpen, setBankDetailsOpen] = useState(false);
  const [bankDetailsPromptOpen, setBankDetailsPromptOpen] = useState(false);
  const [analytics, setAnalytics] = useState({
    dailyEarnings: [],
    weeklyEarnings: [],
    monthlyEarnings: [],
    topCities: [],
    earningsByLocation: [],
    earningsByAppointmentType: [],
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filter, setFilter] = useState({ date: "", appointmentId: "", city: "", state: "", status: "" });
  const [periodFilter, setPeriodFilter] = useState("monthly");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    if (!doctorUser) {
      navigate("/doctor-login");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } };
      try {
        const [earningsRes, breakdownRes, withdrawalsRes, bankRes, analyticsRes] = await Promise.all([
          api.get(`/doctors/earnings/${doctorUser.id}`, config),
          api.get(`/doctors/earnings/breakdown/${doctorUser.id}`, config),
          api.get(`/doctors/withdrawals/${doctorUser.id}`, config),
          api.get(`/doctors/bank-details/${doctorUser.id}`, config),
          api.get(`/doctors/analytics/${doctorUser.id}`, config),
        ]);

        setEarnings(earningsRes.data);
        const breakdownData = breakdownRes.data || [];
        // Sort breakdown by date in descending order (newest first)
        breakdownData.sort((a, b) => new Date(b.date) - new Date(a.date));
        setBreakdown(breakdownData);
        setFilteredBreakdown(breakdownData);
        
        // Sort withdrawals by requestDate in descending order (newest first)
        const withdrawalsData = withdrawalsRes.data || [];
        withdrawalsData.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
        setWithdrawals(withdrawalsData);

        setBankDetails(bankRes.data);
        setAnalytics(analyticsRes.data || {
          dailyEarnings: [],
          weeklyEarnings: [],
          monthlyEarnings: [],
          topCities: [],
          earningsByLocation: [],
          earningsByAppointmentType: [],
        });
      } catch (err) {
        console.error("Data fetch error:", err.response?.data || err.message);
        setSnackbar({ open: true, message: "Failed to load earnings data", severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [doctorUser, navigate]);

  useEffect(() => {
    const handleResize = () => setSidebarOpen(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleWithdrawalRequest = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount > earnings.totalAmountAvailableForWithdrawal || amount < 1000) {
      setSnackbar({ open: true, message: "Invalid amount (Min ₹1000, Max available balance)", severity: "error" });
      return;
    }

    if (!bankDetails || (!bankDetails.accountNumber && !bankDetails.upiId)) {
      setBankDetailsPromptOpen(true);
      return;
    }

    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } };
    try {
      const res = await api.post(`/doctors/withdrawals/${doctorUser.id}`, { amount }, config);
      setEarnings({ ...earnings, totalAmountAvailableForWithdrawal: earnings.totalAmountAvailableForWithdrawal - amount });
      // Add new withdrawal at the top
      setWithdrawals([res.data.withdrawal, ...withdrawals]);
      setWithdrawalOpen(false);
      setWithdrawalAmount("");
      setSnackbar({ open: true, message: "Withdrawal request submitted", severity: "success" });
    } catch (err) {
      console.error("Withdrawal error:", err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || "Failed to request withdrawal", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleBankDetailsSave = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${doctorUser.accessToken}` } };
    try {
      await api.put(`/doctors/bank-details/${doctorUser.id}`, bankDetails, config);
      setBankDetailsOpen(false);
      setSnackbar({ open: true, message: "Bank details updated", severity: "success" });
    } catch (err) {
      console.error("Bank details error:", err.response?.data);
      setSnackbar({ open: true, message: "Failed to update bank details", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToProfile = () => {
    setBankDetailsPromptOpen(false);
    navigate("/doctor-profile");
  };

  const handleDownloadInvoice = async (withdrawalId) => {
    setLoading(true);
    const config = {
      headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      responseType: 'blob',
    };
    try {
      const response = await api.get(`/doctors/invoice/${withdrawalId}`, config);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${withdrawalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Invoice downloaded successfully', severity: 'success' });
    } catch (err) {
      console.error('Invoice download error:', err.response?.data || err.message);
      let errorMessage = 'Failed to download invoice';
      if (err.response && err.response.data) {
        const text = await new Response(err.response.data).text();
        errorMessage = JSON.parse(text)?.error || text;
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
    applyFilters({ ...filter, [name]: value });
  };

  const applyFilters = (currentFilter) => {
    let filtered = [...breakdown];
    if (currentFilter.date) {
      filtered = filtered.filter((item) =>
        new Date(item.date).toLocaleDateString().includes(currentFilter.date)
      );
    }
    if (currentFilter.appointmentId) {
      filtered = filtered.filter((item) =>
        item.appointmentId.toLowerCase().includes(currentFilter.appointmentId.toLowerCase())
      );
    }
    if (currentFilter.city) {
      filtered = filtered.filter((item) =>
        item.city?.toLowerCase().includes(currentFilter.city.toLowerCase())
      );
    }
    if (currentFilter.state) {
      filtered = filtered.filter((item) =>
        item.state?.toLowerCase().includes(currentFilter.state.toLowerCase())
      );
    }
    if (currentFilter.status) {
      filtered = filtered.filter((item) =>
        item.status.toLowerCase() === currentFilter.status.toLowerCase()
      );
    }
    // Maintain descending order after filtering
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFilteredBreakdown(filtered);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredBreakdown.map(item => ({
      Date: new Date(item.date).toLocaleDateString(),
      "Appointment ID": item.appointmentId,
      "Patient Name": item.patientName,
      "State & City": `${item.state}, ${item.city}`,
      "Doctor Fee": item.fee,
      "Doctor Commission": item.commission,
      "Net Earnings": item.netEarnings,
      Status: item.status,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Earnings");
    XLSX.writeFile(workbook, "DoctorEarnings.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Doctor Earnings Report", 14, 10);
    doc.autoTable({
      head: [["Date", "Appointment ID", "Patient Name", "State & City", "Doctor Fee", "Doctor Commission", "Net Earnings", "Status"]],
      body: filteredBreakdown.map(item => [
        new Date(item.date).toLocaleDateString(),
        item.appointmentId,
        item.patientName,
        `${item.state}, ${item.city}`,
        `₹${item.fee}`,
        `₹${item.commission}`,
        `₹${item.netEarnings}`,
        item.status,
      ]),
    });
    doc.save("DoctorEarnings.pdf");
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleWithdrawalClick = () => {
    setWithdrawalOpen(true);
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  if (loading) return (
    <Box className="doctor-earnings-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box className="doctor-earnings-content">
        <Button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </Button>
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading your TureMedicine earnings...</p>
        </div>
      </Box>
    </Box>
  );

  return (
    <Box className="doctor-earnings-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <Box className="doctor-earnings-content">
        <Button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </Button>
        <Typography variant="h4" gutterBottom>Earnings & Payments</Typography>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Earnings Summary</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Tooltip title="Earnings from appointments completed today">
                <Typography>
                  <strong>Today’s Earnings:</strong> ₹{earnings.todaysEarnings || 0}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Previous Earnings</InputLabel>
                <Select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  label="Previous Earnings"
                >
                  <MenuItem value="weekly">Weekly: ₹{earnings.previousEarnings.weekly || 0}</MenuItem>
                  <MenuItem value="monthly">Monthly: ₹{earnings.previousEarnings.monthly || 0}</MenuItem>
                  <MenuItem value="yearly">Yearly: ₹{earnings.previousEarnings.yearly || 0}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography><strong>Total Earnings:</strong> ₹{earnings.totalEarnings || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography><strong>Total Amount Available for Withdrawal:</strong> ₹{earnings.totalAmountAvailableForWithdrawal || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography><strong>Total Withdrawn:</strong> ₹{earnings.totalWithdrawn || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography><strong>Next Payout Date:</strong> {earnings.nextPayoutDate || "TBD"}</Typography>
            </Grid>
          </Grid>

          <Typography variant="h6">Transaction History</Typography>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Date (DD-MM-YYYY)"
                  name="date"
                  value={filter.date}
                  onChange={handleFilterChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Appointment ID"
                  name="appointmentId"
                  value={filter.appointmentId}
                  onChange={handleFilterChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="City"
                  name="city"
                  value={filter.city}
                  onChange={handleFilterChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="State"
                  name="state"
                  value={filter.state}
                  onChange={handleFilterChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={filter.status}
                    onChange={handleFilterChange}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button startIcon={<GetApp />} onClick={exportToExcel}>Export Excel</Button>
                <Button startIcon={<GetApp />} onClick={exportToPDF} sx={{ ml: 1 }}>Export PDF</Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" }, maxHeight: filteredBreakdown.length > 10 ? '400px' : 'auto', overflowY: filteredBreakdown.length > 10 ? 'auto' : 'visible' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Appointment ID</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>State & City</TableCell>
                  <TableCell>Doctor Fee</TableCell>
                  <TableCell>Doctor Commission</TableCell>
                  <TableCell>Net Earnings</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Commission Rate (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBreakdown.map((item) => (
                  <TableRow key={item.appointmentId}>
                    <TableCell>{new Date(item.date).toLocaleDateString()}</TableCell>
                    <TableCell>{item.appointmentId}</TableCell>
                    <TableCell>{item.patientName}</TableCell>
                    <TableCell>{`${item.state}, ${item.city}`}</TableCell>
                    <TableCell>₹{item.fee}</TableCell>
                    <TableCell>₹{item.commission}</TableCell>
                    <TableCell>₹{item.netEarnings}</TableCell>
                    <TableCell>
                      <Chip label={item.status} color={item.status === "paid" ? "success" : "warning"} />
                    </TableCell>
                    <TableCell>{((item.commission / item.fee) * 100).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box className="transaction-mobile" sx={{ display: { xs: "block", md: "none" }, maxHeight: filteredBreakdown.length > 10 ? '400px' : 'auto', overflowY: filteredBreakdown.length > 10 ? 'auto' : 'visible' }}>
            {filteredBreakdown.map((item) => (
              <Paper key={item.appointmentId} className="transaction-card">
                <Box className="transaction-header">
                  <Typography variant="subtitle1">{new Date(item.date).toLocaleDateString()}</Typography>
                  <Chip label={item.status} color={item.status === "paid" ? "success" : "warning"} />
                </Box>
                <Box className="transaction-details">
                  <Typography><strong>Appointment ID:</strong> {item.appointmentId}</Typography>
                  <Typography><strong>Patient:</strong> {item.patientName}</Typography>
                  <Typography><strong>Location:</strong> {`${item.state}, ${item.city}`}</Typography>
                  <Typography><strong>Fee:</strong> ₹{item.fee}</Typography>
                  <Typography><strong>Commission:</strong> ₹{item.commission} ({((item.commission / item.fee) * 100).toFixed(2)}%)</Typography>
                  <Typography><strong>Net Earnings:</strong> ₹{item.netEarnings}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6">Withdrawal Management</Typography>
            <Button
              startIcon={<PaymentIcon />}
              onClick={handleWithdrawalClick}
              disabled={earnings.totalAmountAvailableForWithdrawal < 1000}
            >
              Request Withdrawal (Min ₹1000)
            </Button>
            <Button onClick={() => setBankDetailsOpen(true)} sx={{ ml: 2 }}>Manage Bank/UPI Details</Button>
          </Box>
          <Box sx={{ mt: 2, maxHeight: withdrawals.length > 10 ? '400px' : 'auto', overflowY: withdrawals.length > 10 ? 'auto' : 'visible' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.map((w) => (
                  <TableRow key={w._id}>
                    <TableCell>{new Date(w.requestDate).toLocaleString()}</TableCell>
                    <TableCell>₹{w.amount}</TableCell>
                    <TableCell>
                      <Chip
                        label={w.status}
                        color={w.status === "approved" ? "success" : w.status === "rejected" ? "error" : "warning"}
                      />
                      {w.status === "rejected" && w.rejectionReason && (
                        <Typography variant="caption" sx={{ ml: 1 }}>Reason: {w.rejectionReason}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{w.method}</TableCell>
                    <TableCell>
                      {w.status === "approved" && (
                        <Button startIcon={<Download />} onClick={() => handleDownloadInvoice(w._id)}>
                          Invoice
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="h6" sx={{ mt: 3 }}>Financial Analytics</Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Earnings Trend</Typography>
            <FormControl sx={{ mb: 2, minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                label="Period"
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={
                  periodFilter === "daily"
                    ? analytics.dailyEarnings
                    : periodFilter === "weekly"
                    ? analytics.weeklyEarnings
                    : analytics.monthlyEarnings
                }
              >
                <XAxis dataKey={periodFilter === "daily" ? "date" : "period"} />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="earnings" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>Earnings by Location</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>State & City</TableCell>
                  <TableCell>Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.earningsByLocation.map((loc, index) => (
                  <TableRow key={index}>
                    <TableCell>{`${loc.state}, ${loc.city}`}</TableCell>
                    <TableCell>₹{loc.earnings}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>Earnings by Appointment Type</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.earningsByAppointmentType.map((type, index) => (
                  <TableRow key={index}>
                    <TableCell>{type.type}</TableCell>
                    <TableCell>₹{type.earnings}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <Dialog open={withdrawalOpen} onClose={() => setWithdrawalOpen(false)}>
          <DialogTitle>Request Withdrawal</DialogTitle>
          <DialogContent>
            <Typography>Available Balance: ₹{earnings.totalAmountAvailableForWithdrawal || 0}</Typography>
            <TextField
              label="Amount (Min ₹1000)"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(e.target.value)}
              fullWidth
              type="number"
              sx={{ mt: 2 }}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWithdrawalOpen(false)} disabled={loading}>Cancel</Button>
            <Button onClick={handleWithdrawalRequest} color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Request"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={bankDetailsPromptOpen} onClose={() => setBankDetailsPromptOpen(false)}>
          <DialogTitle>Bank Details Required</DialogTitle>
          <DialogContent>
            <Typography>
              Please update your bank details to proceed with the withdrawal request.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBankDetailsPromptOpen(false)}>Cancel</Button>
            <Button onClick={handleNavigateToProfile} color="primary">
              Update Bank Details
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={bankDetailsOpen} onClose={() => setBankDetailsOpen(false)}>
          <DialogTitle>Manage Bank/UPI Details</DialogTitle>
          <DialogContent>
            <TextField
              label="Account Number"
              value={bankDetails?.accountNumber || ""}
              onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            />
            <TextField
              label="IFSC Code"
              value={bankDetails?.ifscCode || ""}
              onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            />
            <TextField
              label="UPI ID"
              value={bankDetails?.upiId || ""}
              onChange={(e) => setBankDetails({ ...bankDetails, upiId: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            />
            <TextField
              label="Paytm Number"
              value={bankDetails?.paytmNumber || ""}
              onChange={(e) => setBankDetails({ ...bankDetails, paytmNumber: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBankDetailsOpen(false)} disabled={loading}>Cancel</Button>
            <Button onClick={handleBankDetailsSave} color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default DoctorEarnings;